import { useStanQuery } from '@/queries';
import { fetchChartData, fetchChartDataByType } from '@/lib/api';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib/constants';
import type { ChartType } from '../types/analytics.types';
import { keepPreviousData } from '@tanstack/react-query';
/**
 * Base hook for fetching individual chart data
 * Consolidated from multiple similar functions
 */
export function useChartData(
    chartType: ChartType,
    dayRange: number,
    endDate: number,
    periodLength?: number,
    showSumSeries = false
) {
    return useStanQuery(
        {
            queryKey: [QUERY_KEYS.ANALYTICS_CHART, chartType, dayRange, endDate, periodLength, showSumSeries],
            queryFn: () => fetchChartData({ chartType, dayRange, endDate, periodLength, showSumSeries }),
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: 2,
            placeholderData: keepPreviousData,
            meta: {
                feature: SENTRY_FEATURES.ANALYTICS,
                error: { sentryMessage: `[ANALYTICS_CHART] failed-to-fetch-${chartType}` },
            },
        },
        true
    );
}

/**
 * Base hook for fetching multiple chart data types at once
 * Consolidated from multiple similar functions
 */
export function useMultipleChartData(
    chartTypes: ChartType[],
    dayRange: number,
    endDate: number,
    periodLength?: number,
    showSumSeries = false
) {
    return useStanQuery(
        {
            queryKey: [
                QUERY_KEYS.ANALYTICS_MULTIPLE_CHARTS,
                chartTypes,
                dayRange,
                endDate,
                periodLength,
                showSumSeries,
            ],
            queryFn: () => fetchChartDataByType(chartTypes, dayRange, endDate, periodLength, showSumSeries),
            enabled: dayRange > 0 && !!endDate && chartTypes.length > 0,
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: 2,
            placeholderData: keepPreviousData,
            meta: {
                feature: SENTRY_FEATURES.ANALYTICS,
                error: { sentryMessage: '[ANALYTICS_MULTIPLE_CHARTS] failed-to-fetch-multiple-charts' },
            },
        },
        true
    );
}
